import { api } from './api';

/**
 * Push notifications in the browser: register the service worker, ask for
 * permission, and hand the resulting subscription to the server.
 *
 * iPhones only allow web push for a site added to the home screen (iOS 16.4
 * and later), so on iOS in a normal Safari tab the status is 'install' and the
 * interface explains how, instead of offering a button that cannot work.
 */

export type PushStatus = 'unsupported' | 'install' | 'denied' | 'off' | 'on';

const supported = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const isIos = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS reports itself as a Mac with a touch screen.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const isStandalone = (): boolean =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

/** Registered once per page load; harmless where push is not supported. */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);
  registration ??= navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => null);
  return registration;
}

export async function pushStatus(): Promise<PushStatus> {
  if (isIos() && !isStandalone()) return 'install';
  if (!supported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const worker = await registerServiceWorker();
  const subscription = await worker?.pushManager.getSubscription();
  return subscription && Notification.permission === 'granted' ? 'on' : 'off';
}

const base64ToBytes = (value: string): Uint8Array<ArrayBuffer> => {
  const padded = `${value}${'='.repeat((4 - (value.length % 4)) % 4)}`.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
};

/**
 * Asks for permission (this must run from a tap) and subscribes this device.
 * Returns the resulting status, so the caller can explain a refusal.
 */
export async function enablePush(): Promise<PushStatus> {
  const status = await pushStatus();
  if (status === 'install' || status === 'unsupported' || status === 'denied') return status;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const worker = await registerServiceWorker();
  if (!worker) return 'unsupported';
  const { publicKey } = await api.get<{ publicKey: string }>('/push/key', { anonymous: true });
  const subscription =
    (await worker.pushManager.getSubscription()) ??
    (await worker.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64ToBytes(publicKey) }));
  const json = subscription.toJSON();
  await api.post('/push/subscriptions', { endpoint: json.endpoint, keys: json.keys });
  return 'on';
}

/** Stops push on this device, here and on the server. */
export async function disablePush(): Promise<void> {
  const worker = await registerServiceWorker();
  const subscription = await worker?.pushManager.getSubscription();
  if (!subscription) return;
  await api.delete('/push/subscriptions', { endpoint: subscription.endpoint }).catch(() => undefined);
  await subscription.unsubscribe();
}

/**
 * The server keeps a device against whoever last signed in on it. Signing in
 * again re-sends it, so a device that was switched on stays pointed at the
 * right person.
 */
export async function resyncPush(): Promise<void> {
  if (!supported() || Notification.permission !== 'granted') return;
  const worker = await registerServiceWorker();
  const subscription = await worker?.pushManager.getSubscription();
  if (!subscription) return;
  const json = subscription.toJSON();
  await api.post('/push/subscriptions', { endpoint: json.endpoint, keys: json.keys }).catch(() => undefined);
}

/** Called when the service worker relays a push to an open tab. */
export function onPushMessage(listener: (message: { title: string; body?: string; url: string }) => void): () => void {
  if (!('serviceWorker' in navigator)) return () => undefined;
  const handler = (event: MessageEvent) => {
    const data = event.data as { type?: string; title?: string; body?: string; url?: string } | null;
    if (data?.type === 'bedly:notification' && data.title) {
      listener({ title: data.title, body: data.body, url: data.url ?? '/notifications' });
    }
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}

/**
 * Signing out: the server forgets this device for this person, so a shared
 * computer stops showing their messages. The browser keeps its permission, and
 * the next sign-in re-attaches it (see resyncPush).
 */
export async function detachPush(): Promise<void> {
  if (!supported()) return;
  const worker = await registerServiceWorker();
  const subscription = await worker?.pushManager.getSubscription();
  if (!subscription) return;
  await api.delete('/push/subscriptions', { endpoint: subscription.endpoint }).catch(() => undefined);
}
